/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

// Note: The listingFields come from listingFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public' or 'private'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - listingTypeConfig (optional):  Relationship configuration against listing types.
 *   - limitToListingTypeIds:         Indicator whether this listing field is relevant to a limited set of listing types.
 *   - listingTypeIds:                An array of listing types, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToListingTypeIds is true.
 * - categoryConfig (optional):     Relationship configuration against categories.
 *   - limitToCategoryIds:            Indicator whether this listing field is relevant to a limited set of categories.
 *   - categoryIds:                   An array of categories, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToCategoryIds is true.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Sharetribe CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
/////////////////////////////////////////////////////////
// Category tree (usedemoto)                           //
/////////////////////////////////////////////////////////

/**
 * LOCAL FALLBACK for the category tree.
 *
 * IMPORTANT: HOSTED CONSOLE CONFIG ALWAYS WINS.
 * In production, categories come exclusively from the hosted asset
 * '/listings/listing-categories.json' (authored in Sharetribe Console).
 * This array is only consulted by src/util/configHelpers.js when that hosted
 * asset is missing or empty, so that `yarn dev` has a working category tree
 * before Console has been set up. Whenever the hosted asset exists, this array
 * is ignored entirely — see `mergeConfig` / `getLocalCategoriesFallback`.
 *
 * Shape must match what `validateCategoryConfig` expects (hosted-asset shape):
 *   { id: string, name: string, subcategories: Array<same shape> }
 * Note this is the ONE place in this file that uses the hosted-asset shape.
 * Everything else below uses the LOCAL shape (see doc comments).
 *
 * Depth matters: the tree depth determines `categoryLevelKeys`
 * (categoryLevel1..categoryLevel3) in `getBuiltInCategorySpecs`. Two levels here
 * => categoryLevel1 + categoryLevel2 public-data keys and URL params.
 * Keep these ids in sync with docs/CONSOLE_SETUP.md and with the hosted asset.
 */
export const categories = [
  {
    id: 'emotos',
    name: 'Electric motorcycles',
    subcategories: [
      {
        id: 'sur-ron',
        name: 'Sur-Ron',
        subcategories: [
          { id: 'light-bee', name: 'Light Bee X', subcategories: [] },
          { id: 'ultra-bee', name: 'Ultra Bee', subcategories: [] },
          { id: 'storm-bee', name: 'Storm Bee', subcategories: [] },
          { id: 'other-sur-ron', name: 'Other Sur-Ron Model', subcategories: [] },
        ],
      },
      {
        id: 'talaria',
        name: 'Talaria',
        subcategories: [
          { id: 'sting-mx4', name: 'Sting R / MX4', subcategories: [] },
          { id: 'xxx', name: 'XXX', subcategories: [] },
          { id: 'dragon', name: 'Dragon', subcategories: [] },
          { id: 'mx5', name: 'Sting Pro / MX5', subcategories: [] },
          { id: 'other-talaria', name: 'Other Talaria Model', subcategories: [] },
        ],
      },
      {
        id: 'e-ride-pro',
        name: 'E Ride Pro',
        subcategories: [
          { id: 'pro-ss', name: 'Pro SS', subcategories: [] },
          { id: 'pro-s', name: 'Pro S', subcategories: [] },
          { id: 'other-erp', name: 'Other E Ride Pro Model', subcategories: [] },
        ],
      },
      {
        id: '79bike',
        name: '79Bike',
        subcategories: [
          { id: 'falcon-m', name: 'Falcon M', subcategories: [] },
          { id: 'other-79bike', name: 'Other 79Bike Model', subcategories: [] },
        ],
      },
      {
        id: 'stark',
        name: 'Stark Future',
        subcategories: [
          { id: 'varg', name: 'Varg', subcategories: [] },
          { id: 'other-stark', name: 'Other Stark Model', subcategories: [] },
        ],
      },
      {
        id: 'segway',
        name: 'Segway / Ninebot',
        subcategories: [
          { id: 'segway-x', name: 'X160 / X260', subcategories: [] },
          { id: 'other-segway', name: 'Other Segway Model', subcategories: [] },
        ],
      },
      {
        id: 'altis',
        name: 'Altis',
        subcategories: [
          { id: 'sigma-mx', name: 'Sigma MX', subcategories: [] },
          { id: 'sigma', name: 'Sigma', subcategories: [] },
          { id: 'alpha', name: 'Alpha', subcategories: [] },
          { id: 'delta', name: 'Delta', subcategories: [] },
          { id: 'altis-other', name: 'Other Altis Model', subcategories: [] },
        ],
      },
      {
        id: 'rawrr',
        name: 'Rawrr',
        subcategories: [
          { id: 'mantis', name: 'Mantis', subcategories: [] },
          { id: 'mantis-x', name: 'Mantis X', subcategories: [] },
          { id: 'mantis-x-pro', name: 'Mantis X Pro', subcategories: [] },
          { id: 'rawrr-other', name: 'Other Rawrr Model', subcategories: [] },
        ],
      },
      {
        id: 'other-brand',
        name: 'Other Brand',
        subcategories: [
          { id: 'other-model', name: 'Other / Custom Model', subcategories: [] },
        ],
      },
    ],
  },
  {
    id: 'parts',
    name: 'Parts & accessories',
    subcategories: [
      { id: 'parts-batteries', name: 'Batteries & chargers', subcategories: [] },
      { id: 'parts-drivetrain', name: 'Controllers & motors', subcategories: [] },
      { id: 'parts-suspension', name: 'Suspension', subcategories: [] },
      { id: 'parts-wheels', name: 'Wheels & tires', subcategories: [] },
      { id: 'parts-brakes', name: 'Brakes', subcategories: [] },
      { id: 'parts-body', name: 'Body & plastics', subcategories: [] },
      { id: 'parts-electronics', name: 'Electronics & displays', subcategories: [] },
      { id: 'parts-gear', name: 'Gear & accessories', subcategories: [] },
      { id: 'other-parts', name: 'Other parts & accessories', subcategories: [] },
    ],
  },
  {
    id: 'other-category',
    name: 'Other / Miscellaneous',
    subcategories: [
      { id: 'tools-transport', name: 'Tools, gear & transport', subcategories: [] },
      { id: 'other-items', name: 'Other items', subcategories: [] },
    ],
  },
];

// usedemoto listing fields.
//
// PRODUCTION NOTE: on a Sharetribe-hosted marketplace these are IGNORED unless
// NODE_ENV === 'development' AND `mergeDefaultTypesAndFieldsForDebugging(true)`
// is used in src/util/configHelpers.js (it is, for the listing config).
// The '/listings/listing-fields.json' hosted asset is the production source of truth.
//
// Shape traps that silently DROP a field during validation (validListingFields):
//  - use `saveConfig.isRequired` here (LOCAL shape); the hosted asset uses `required`.
//  - every filterConfig/showConfig/saveConfig object you include MUST have its own
//    `label` string; there is no top-level `label` fallback in the local shape.
//  - `numberConfig: { minimum, maximum }` is required for every 'long' field
//    (it becomes top-level minimum/maximum/step after validation).
//  - `categoryConfig.categoryIds` must reference ids that exist in the category tree
//    actually in use, otherwise the whole field is dropped.
// Fields are scoped to the PARENT category id ('emotos' / 'parts') on purpose, so that they
// apply to every subcategory underneath without having to be repeated per subcategory.
// Note that the "Category & condition" wizard step only renders them once the provider has
// drilled down to a leaf category, so that the visible set of fields can't change mid-fill.
export const listingFields = [
  {
    key: 'condition',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'new', label: 'New' },
      { option: 'like-new', label: 'Like new' },
      { option: 'used-good', label: 'Used - Good condition' },
      { option: 'used', label: 'Used' },
      { option: 'for-parts', label: 'For parts / Not working' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Condition',
      group: 'primary',
    },
    showConfig: {
      label: 'Condition',
      isDetail: true,
    },
    saveConfig: {
      label: 'Condition',
      placeholderMessage: 'Select condition...',
      isRequired: true,
      requiredMessage: 'You need to select a condition.',
    },
  },
  {
    key: 'fitment',
    scope: 'public',
    schemaType: 'enum',
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['parts', 'part'],
    },
    enumOptions: [
      { option: 'sur-ron-lbx', label: 'Sur-Ron Light Bee X' },
      { option: 'sur-ron-ultra', label: 'Sur-Ron Ultra Bee' },
      { option: 'talaria-sting', label: 'Talaria Sting / MX4 / MX5' },
      { option: 'talaria-xxx', label: 'Talaria XXX' },
      { option: 'eride-pro', label: 'E Ride Pro SS / S' },
      { option: 'universal', label: 'Universal Fit / Multiple' },
      { option: 'other-fitment', label: 'Other / Custom' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Fitment (Bike Model)',
      group: 'primary',
    },
    showConfig: {
      label: 'Fitment',
      isDetail: true,
    },
    saveConfig: {
      label: 'Fits bike model (optional)',
      placeholderMessage: 'Select which bike this fits...',
      isRequired: false,
    },
  },
  {
    key: 'modelYear',
    scope: 'public',
    schemaType: 'long',
    label: 'Model year',
    numberConfig: {
      minimum: 2010,
      maximum: 2030,
    },
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['emoto', 'emotos'],
    },
    filterConfig: {
      indexForSearch: true,
      label: 'Model year',
      group: 'secondary',
    },
    showConfig: {
      label: 'Model year',
      isDetail: true,
    },
    saveConfig: {
      label: 'Model year',
      placeholderMessage: 'e.g. 2023',
      isRequired: true,
      requiredMessage: 'You need to add the model year.',
    },
  },
  {
    key: 'odometerMiles',
    scope: 'public',
    schemaType: 'long',
    label: 'Odometer (miles)',
    numberConfig: {
      minimum: 0,
      maximum: 30000,
    },
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['emoto', 'emotos'],
    },
    filterConfig: {
      indexForSearch: true,
      label: 'Odometer (miles)',
      group: 'secondary',
    },
    showConfig: {
      label: 'Odometer (miles)',
      isDetail: true,
    },
    saveConfig: {
      label: 'Odometer (miles)',
      placeholderMessage: 'e.g. 850',
      isRequired: false,
    },
  },
  {
    key: 'batteryCycles',
    scope: 'public',
    schemaType: 'long',
    label: 'Battery charge cycles',
    numberConfig: {
      minimum: 0,
      maximum: 5000,
    },
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['emoto', 'emotos'],
    },
    filterConfig: {
      indexForSearch: true,
      label: 'Battery charge cycles',
      group: 'secondary',
    },
    showConfig: {
      label: 'Battery charge cycles',
      isDetail: true,
    },
    saveConfig: {
      label: 'Battery charge cycles',
      placeholderMessage: 'Total full charge cycles, e.g. 85',
      isRequired: false,
    },
  },
  {
    key: 'batteryHealthPercent',
    scope: 'public',
    schemaType: 'long',
    label: 'Battery health (%)',
    numberConfig: {
      minimum: 0,
      maximum: 100,
    },
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['emoto', 'emotos'],
    },
    filterConfig: {
      indexForSearch: true,
      label: 'Battery health (%)',
      group: 'secondary',
    },
    showConfig: {
      label: 'Battery health (%)',
      isDetail: true,
    },
    saveConfig: {
      label: 'Battery health (%)',
      placeholderMessage: 'Estimated remaining capacity, e.g. 92',
      isRequired: false,
    },
  },
  {
    key: 'batteryCapacityWh',
    scope: 'public',
    schemaType: 'long',
    label: 'Battery capacity (Wh)',
    numberConfig: {
      minimum: 200,
      maximum: 25000,
    },
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['emoto', 'emotos'],
    },
    filterConfig: {
      indexForSearch: true,
      label: 'Battery capacity (Wh)',
      group: 'secondary',
    },
    showConfig: {
      label: 'Battery capacity (Wh)',
      isDetail: true,
    },
    saveConfig: {
      label: 'Battery capacity (Wh)',
      placeholderMessage: 'Volts × amp-hours, e.g. 60V 40Ah = 2400',
      isRequired: false,
    },
  },
  {
    key: 'motorPowerW',
    scope: 'public',
    schemaType: 'long',
    label: 'Motor power (W)',
    numberConfig: {
      minimum: 250,
      maximum: 30000,
    },
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['emoto', 'emotos'],
    },
    filterConfig: {
      indexForSearch: true,
      label: 'Motor power (W)',
      group: 'primary',
    },
    showConfig: {
      label: 'Motor power (W)',
      isDetail: true,
    },
    saveConfig: {
      label: 'Motor power (W)',
      placeholderMessage: 'Peak output in watts, e.g. 6000',
      isRequired: false,
    },
  },
  {
    key: 'topSpeedMph',
    scope: 'public',
    schemaType: 'long',
    label: 'Top speed (mph)',
    numberConfig: {
      minimum: 10,
      maximum: 120,
    },
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['emoto', 'emotos'],
    },
    filterConfig: {
      indexForSearch: true,
      label: 'Top speed (mph)',
      group: 'secondary',
    },
    showConfig: {
      label: 'Top speed (mph)',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top speed (mph)',
      placeholderMessage: 'e.g. 45',
      isRequired: false,
    },
  },
  {
    key: 'streetLegal',
    scope: 'public',
    schemaType: 'enum',
    label: 'Street legal',
    enumOptions: [{ option: 'yes', label: 'Yes' }, { option: 'no', label: 'No' }],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['emoto', 'emotos'],
    },
    filterConfig: {
      indexForSearch: false,
      label: 'Street legal',
    },
    showConfig: {
      label: 'Street legal',
      isDetail: true,
    },
    saveConfig: {
      label: 'Is this emoto street legal as sold?',
      placeholderMessage: 'Select yes or no…',
      isRequired: false,
    },
  },
  {
    key: 'titleStatus',
    scope: 'public',
    schemaType: 'enum',
    label: 'Title / VIN status',
    enumOptions: [
      { option: 'clean-title', label: 'Clean title' },
      { option: 'vin-no-title', label: 'Has VIN, no title' },
      { option: 'bill-of-sale', label: 'Bill of sale only' },
      { option: 'salvage', label: 'Salvage / rebuilt title' },
      { option: 'off-road-only', label: 'Off-road only, no VIN' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['emoto', 'emotos'],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Title / VIN status',
      group: 'secondary',
    },
    showConfig: {
      label: 'Title / VIN status',
      isDetail: true,
    },
    saveConfig: {
      label: 'Title / VIN status',
      placeholderMessage: 'Select an option…',
      isRequired: false,
    },
  },
  {
    key: 'modifications',
    scope: 'public',
    schemaType: 'multi-enum',
    label: 'Modifications',
    enumOptions: [
      { option: 'stock', label: 'Stock / unmodified' },
      { option: 'aftermarket-controller', label: 'Aftermarket controller' },
      { option: 'upgraded-battery', label: 'Upgraded battery' },
      { option: 'suspension-upgrade', label: 'Suspension upgrade' },
      { option: 'brake-upgrade', label: 'Brake upgrade' },
      { option: 'wheel-tire-swap', label: 'Wheel / tire swap' },
      { option: 'gearing-change', label: 'Gearing change' },
      { option: 'street-legal-kit', label: 'Street legal kit' },
      { option: 'seat-ergonomics', label: 'Seat / ergonomics' },
      { option: 'cooling-mods', label: 'Cooling mods' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['emoto', 'emotos'],
    },
    filterConfig: {
      indexForSearch: true,
      label: 'Modifications',
      searchMode: 'has_any',
      group: 'secondary',
    },
    showConfig: {
      label: 'Modifications',
      unselectedOptions: false,
    },
    saveConfig: {
      label: 'Modifications',
      placeholderMessage: 'Select all that apply…',
      isRequired: false,
    },
  },
  {
    key: 'includedExtras',
    scope: 'public',
    schemaType: 'multi-enum',
    label: 'Included extras',
    enumOptions: [
      { option: 'spare-battery', label: 'Spare battery' },
      { option: 'charger', label: 'Charger' },
      { option: 'fast-charger', label: 'Fast charger' },
      { option: 'tools', label: 'Tools / spares' },
      { option: 'riding-gear', label: 'Riding gear' },
      { option: 'spare-tires', label: 'Spare tires' },
      { option: 'spare-sprockets', label: 'Spare sprockets / chain' },
      { option: 'manuals-receipts', label: 'Manuals & receipts' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['emoto', 'emotos'],
    },
    filterConfig: {
      indexForSearch: true,
      label: 'Included extras',
      searchMode: 'has_all',
      group: 'secondary',
    },
    showConfig: {
      label: 'Included extras',
      unselectedOptions: false,
    },
    saveConfig: {
      label: 'Included extras',
      placeholderMessage: 'Select all that apply…',
      isRequired: false,
    },
  },
  {
    key: 'knownIssues',
    scope: 'public',
    schemaType: 'text',
    label: 'Known issues',
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['emoto', 'emotos'],
    },
    filterConfig: {
      indexForSearch: false,
      label: 'Known issues',
    },
    showConfig: {
      label: 'Known issues',
    },
    saveConfig: {
      label: 'Known issues',
      placeholderMessage:
        'Anything a buyer should know: soft brakes, controller error codes, cosmetic damage…',
      isRequired: false,
    },
  },
  {
    key: 'partType',
    scope: 'public',
    schemaType: 'enum',
    label: 'Part type',
    enumOptions: [
      { option: 'battery-pack', label: 'Battery pack' },
      { option: 'charger', label: 'Charger' },
      { option: 'bms-wiring', label: 'BMS & wiring' },
      { option: 'controller', label: 'Controller' },
      { option: 'motor', label: 'Motor' },
      { option: 'fork', label: 'Fork' },
      { option: 'rear-shock', label: 'Rear shock' },
      { option: 'wheels-rims', label: 'Wheels & rims' },
      { option: 'tires-tubes', label: 'Tires & tubes' },
      { option: 'brakes', label: 'Brakes' },
      { option: 'plastics-body', label: 'Plastics & bodywork' },
      { option: 'frame-swingarm', label: 'Frame & swingarm' },
      { option: 'seat', label: 'Seat' },
      { option: 'display-electronics', label: 'Display & electronics' },
      { option: 'lighting', label: 'Lighting' },
      { option: 'sprockets-chain', label: 'Sprockets & chain' },
      { option: 'riding-gear', label: 'Riding gear' },
      { option: 'tools', label: 'Tools' },
      { option: 'other', label: 'Other part' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['parts', 'part'],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Part type',
      group: 'primary',
    },
    showConfig: {
      label: 'Part type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Part type',
      placeholderMessage: 'Select a part type…',
      isRequired: true,
      requiredMessage: 'You need to select a part type.',
    },
  },
  {
    key: 'fitsModels',
    scope: 'public',
    schemaType: 'multi-enum',
    label: 'Fits models',
    enumOptions: [
      { option: 'surron-light-bee', label: 'Sur-Ron Light Bee' },
      { option: 'surron-ultra-bee', label: 'Sur-Ron Ultra Bee' },
      { option: 'surron-storm-bee', label: 'Sur-Ron Storm Bee' },
      { option: 'talaria-sting', label: 'Talaria Sting' },
      { option: 'talaria-xxx', label: 'Talaria XXX' },
      { option: 'talaria-mx', label: 'Talaria MX3 / MX4' },
      { option: 'segway-x160-x260', label: 'Segway X160 / X260' },
      { option: 'bonnell', label: 'Bonnell' },
      { option: 'ronin', label: 'Ronin / Bonnell' },
      { option: 'stark-varg', label: 'Stark Varg' },
      { option: 'e-ride-pro-ss', label: 'E Ride Pro SS' },
      { option: 'arctic-leopard', label: 'Arctic Leopard' },
      { option: 'universal', label: 'Universal fit' },
      { option: 'other', label: 'Other / see description' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['parts', 'part'],
    },
    filterConfig: {
      indexForSearch: true,
      label: 'Fits models',
      searchMode: 'has_any',
      group: 'primary',
    },
    showConfig: {
      label: 'Fits models',
      unselectedOptions: false,
    },
    saveConfig: {
      label: 'Fits models',
      placeholderMessage: 'Select every model this part fits…',
      isRequired: true,
      requiredMessage: 'You need to select at least one model.',
    },
  },
  {
    key: 'partCondition',
    scope: 'public',
    schemaType: 'enum',
    label: 'Part condition',
    enumOptions: [
      { option: 'new-in-box', label: 'New in box' },
      { option: 'new-open-box', label: 'New, open box' },
      { option: 'used-excellent', label: 'Used - excellent' },
      { option: 'used-good', label: 'Used - good' },
      { option: 'used-worn', label: 'Used - worn' },
      { option: 'for-parts', label: 'For parts / not working' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['parts', 'part'],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Part condition',
      group: 'secondary',
    },
    showConfig: {
      label: 'Part condition',
      isDetail: true,
    },
    saveConfig: {
      label: 'Part condition',
      placeholderMessage: 'Select a condition…',
      isRequired: true,
      requiredMessage: 'You need to select the part condition.',
    },
  },
  {
    key: 'partOrigin',
    scope: 'public',
    schemaType: 'enum',
    label: 'OEM or aftermarket',
    enumOptions: [
      { option: 'oem', label: 'OEM' },
      { option: 'aftermarket', label: 'Aftermarket' },
      { option: 'oem-replica', label: 'OEM replica' },
      { option: 'unknown', label: 'Unknown' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['parts', 'part'],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'OEM or aftermarket',
      group: 'secondary',
    },
    showConfig: {
      label: 'OEM or aftermarket',
      isDetail: true,
    },
    saveConfig: {
      label: 'OEM or aftermarket',
      placeholderMessage: 'Select an option…',
      isRequired: false,
    },
  },
  {
    key: 'partNumber',
    scope: 'public',
    schemaType: 'text',
    label: 'Part number',
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['parts', 'part'],
    },
    filterConfig: {
      indexForSearch: false,
      label: 'Part number',
    },
    showConfig: {
      label: 'Part number',
    },
    saveConfig: {
      label: 'Part number',
      placeholderMessage: 'Manufacturer part number, if known',
      isRequired: false,
    },
  },
  {
    key: 'itemSize',
    scope: 'public',
    schemaType: 'text',
    label: 'Size / Dimensions',
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['parts', 'part'],
    },
    filterConfig: {
      indexForSearch: false,
      label: 'Size / Dimensions',
    },
    showConfig: {
      label: 'Size / Dimensions',
    },
    saveConfig: {
      label: 'Size / Dimensions (optional)',
      placeholderMessage: 'e.g. Medium, Large, 19", 220mm, etc.',
      isRequired: false,
    },
  },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: The listingTypes come from listingTypes asset nowadays by default.
//         To use this built-in configuration, you need to change the overwrite from configHelper.js
//         (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem', 'multipleItems', 'infiniteOneItem', and 'infiniteMultipleItems'.
 *                        Default: 'multipleItems'.
 * - availabilityType     This is relevant only to listings using default-booking process.
 *                        If set to 'oneSeat', seat management is not showed and the listing is
 *                        considered per person (seat = 1).
 *                        Possible values: 'oneSeat' and 'multipleSeats'.
 *                        Default: 'oneSeat'.
 * - priceVariations      This is relevant only to listings using default-booking process.
 *   - enabled:             If set to true, price variations are enabled.
 *                          Default: false.
 * - defaultListingFields These are tied to transaction processes. Different processes have different flags.
 *                        E.g. default-inquiry can toggle price and location to true/false value to indicate,
 *                        whether price (or location) tab should be shown. If defaultListingFields.price is not
 *                        explicitly set to _false_, price will be shown.
 *                        If the location or pickup is not used, listing won't be returned with location search.
 *                        Use keyword search as main search type if location is not enforced.
 *                        The payoutDetails flag allows provider to bypass setting of payout details.
 *                        Note: customers can't order listings, if provider has not set payout details! Monitor
 *                        providers who have not set payout details and contact them to ensure that they add the details.
 * - transactionFields    You can define an array of custom transaction fields for each listing type. Each transaction field
 *                        should have the following attributes:
 *                        - key (string)
 *                        - label (string)
 *                        - showTo (string, options: 'customer', 'provider'). Option 'provider' is only used for negotiation process.
 *                        - schemaType (string, options: 'enum', 'multi-enum', 'text', 'long', 'boolean', 'youtubeVideoUrl')
 *                        - saveConfig (object, optional,  { required: true })
 *                        - schema specific attributes:
 *                          - numberConfig (object, for schemaType: 'long'): { minimum: number, maximum: number }
 *                          - enumOptions (array, for schemaType: 'enum', 'multi-enum'): [{ label: string, option: string }]
 */

// usedemoto listing types.
//
// PRODUCTION NOTE: same as listingFields — '/listings/listing-types.json' from Console
// wins in production. These local values only take effect in development.
//
// Only transaction processes that exist in src/transactions/transaction.js are allowed;
// `validListingTypes` drops any type whose process name, alias and unitType don't all match.
export const listingTypes = [
  {
    listingType: 'emoto-sale',
    label: 'Sell an emoto',
    transactionType: {
      process: 'default-purchase',
      alias: 'default-purchase/release-1',
      unitType: 'item',
    },
    stockType: 'oneItem',
    defaultListingFields: {
      description: true,
      shipping: true,
      pickup: true,
      payoutDetails: false,
    },
  },
  {
    listingType: 'parts-sale',
    label: 'Sell parts & accessories',
    transactionType: {
      process: 'default-purchase',
      alias: 'default-purchase/release-1',
      unitType: 'item',
    },
    stockType: 'multipleItems',
    defaultListingFields: {
      description: true,
      shipping: true,
      pickup: true,
      payoutDetails: false,
    },
  },
  {
    listingType: 'other-rides-sale',
    label: 'Sell other electric rides',
    transactionType: {
      process: 'default-purchase',
      alias: 'default-purchase/release-1',
      unitType: 'item',
    },
    stockType: 'oneItem',
    defaultListingFields: {
      description: true,
      shipping: true,
      pickup: true,
      payoutDetails: false,
    },
  },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;
